import React from 'react';
import {
  evaluateCaptionPreset, evaluateMotionElement, fitCaptionFontSize, motionElementLocalTime, resolveMotionCaptions, splitMotionText,
} from '@/lib/motion';
import type {
  CaptionMotionState, MotionCaptionWordStyle, MotionElement, MotionTextStyle, ResolvedMotionCaption,
} from '@/lib/motion';
import {
  motionTextStyle, motionTransform, paintToCss, textFillCss, textShadowCss, textStrokeCss,
} from './renderStyle';

function TextStyleContent({ style, children, tier, keepWords }: { style: MotionTextStyle; children: React.ReactNode; tier?: string; keepWords?: boolean }) {
  // Caption lines break only between words (a long word shrinks its line).
  return <div data-testid="motion-text" data-motion-caption-tier={tier}
    style={{ ...motionTextStyle(style), ...(keepWords ? { overflowWrap: 'normal', wordBreak: 'keep-all' } : {}) }}>{children}</div>;
}

export function MotionText({ authored, evaluated, time }: { authored: MotionElement; evaluated: MotionElement; time: number }) {
  const split = authored.split && authored.split !== 'none';
  // Dynamic text is computed once. Parts then receive only their own staggered preset,
  // avoiding a second counter/typewriter pass or a second application of the parent transform.
  const parts = split ? splitMotionText({ ...authored, content: evaluated.content }) : [];
  const box = evaluated.box;
  return <div style={{ width: evaluated.maxWidth, maxWidth: evaluated.maxWidth,
    padding: box?.padding, borderRadius: box?.radius, background: paintToCss(box?.background), boxSizing: 'border-box' }}>
    <TextStyleContent style={evaluated}>
      {split ? parts.map((part, index) => {
        const partMotion = evaluateMotionElement({ id: part.id, type: 'text', start: part.start, end: part.end, speed: authored.speed, loop: authored.loop,
          enter: part.enter?.type === 'typewriter' ? undefined : part.enter,
          exit: part.exit?.type === 'typewriter' ? undefined : part.exit }, time);
        const clip = typeof partMotion.clip === 'object' ? partMotion.clip : undefined;
        return <React.Fragment key={part.id}>
          {index > 0 && (authored.split === 'line' ? <br /> : authored.split === 'word' ? ' ' : null)}
          <span data-motion-text-part={index} style={{ display: 'inline-block', opacity: partMotion.opacity,
            transform: `translate(${partMotion.x ?? 0}px, ${partMotion.y ?? 0}px) ${motionTransform(partMotion)}`,
            clipPath: clip ? `inset(${(clip.top ?? 0) / 1920 * 100}% ${(clip.right ?? 0) / 1080 * 100}% ${(clip.bottom ?? 0) / 1920 * 100}% ${(clip.left ?? 0) / 1080 * 100}%)` : undefined,
            filter: partMotion.blur ? `blur(${partMotion.blur}px)` : undefined }}>{part.content}</span>
        </React.Fragment>;
      }) : evaluated.content}
    </TextStyleContent>
  </div>;
}

/** Only the parts a preset actually changes: a word at rest gets no
 *  transform or filter (each would make it its own compositing layer). */
function captionStateStyle(state: CaptionMotionState | null): React.CSSProperties {
  if (!state) return {};
  const transform = [
    ...(state.perspective ? [`perspective(${state.perspective}px)`] : []),
    ...(state.x || state.y ? [`translate(${state.x}px, ${state.y}px)`] : []),
    ...(state.rotateY ? [`rotateY(${state.rotateY}deg)`] : []),
    ...(state.scale !== 1 ? [`scale(${state.scale})`] : []),
    ...(state.skewX ? [`skewX(${state.skewX}deg)`] : []),
  ];
  return {
    opacity: Math.round(state.opacity * 1e4) / 1e4,
    ...(transform.length ? { transform: transform.join(' ') } : {}),
    ...(state.blur ? { filter: `blur(${state.blur}px)` } : {}),
    ...(state.clip ? { clipPath: `inset(${state.clip.top}% ${state.clip.right}% ${state.clip.bottom}% ${state.clip.left}%)` } : {}),
  };
}

/** The spoken/active word changes over its tier style: color (or gradient),
 *  outline, glow, a highlight box and a scale. The box's padding is offset by
 *  negative margins, so highlighting never moves the other words. */
function wordStyle(style: MotionCaptionWordStyle | undefined): { css: React.CSSProperties; scale: number } {
  if (!style) return { css: {}, scale: 1 };
  const css: React.CSSProperties = { ...textFillCss(style.fill) };
  if (style.stroke) css.WebkitTextStroke = textStrokeCss(style.stroke, style.strokeWidth);
  if (style.shadow) css.textShadow = textShadowCss(style.shadow);
  if (style.opacity !== undefined) css.opacity = style.opacity;
  if (style.background !== undefined && typeof style.fill !== 'object') {
    css.background = paintToCss(style.background);
    css.borderRadius = style.radius;
    if (style.padding) {
      css.padding = `0 ${style.padding}px`;
      css.margin = `0 -${style.padding}px`;
    }
    css.boxDecorationBreak = 'clone';
  }
  return { css, scale: style.scale ?? 1 };
}

const growth = (style: MotionCaptionWordStyle | undefined) => ({
  scale: style?.scale ?? 1,
  padding: style?.background !== undefined ? style.padding ?? 0 : 0,
});

export function MotionCaptions({ authored, evaluated, time }: { authored: MotionElement; evaluated: MotionElement; time: number }) {
  const words = resolveMotionCaptions(authored, time);
  const local = motionElementLocalTime(authored, time);
  const lines: { style: ResolvedMotionCaption['style']; words: ResolvedMotionCaption[] }[] = [];
  for (const word of words) {
    const previous = lines.find((line) => line.style.id === word.style.id);
    if (previous) previous.words.push(word);
    else lines.push({ style: word.style, words: [word] });
  }
  const maxWidth = evaluated.maxWidth ?? 900;
  // The cue as a whole enters and leaves with cueEnter/cueExit.
  const cue = words.length && (authored.cueEnter || authored.cueExit)
    ? evaluateCaptionPreset({ start: words[0].cueStart, end: words[0].cueEnd, enter: authored.cueEnter, exit: authored.cueExit }, local)
    : null;
  return <div data-testid="motion-captions" style={{ width: maxWidth, display: 'flex', flexDirection: 'column', alignItems: 'stretch', ...captionStateStyle(cue) }}>
    {lines.map((line, index) => {
      const style = { ...evaluated, ...line.style };
      // A gradient is painted on each word: a parent's text clip does not
      // reach words that animate on their own layer.
      const gradient = typeof style.fill === 'object' ? style.fill : undefined;
      const grown = line.words.flatMap((word) => [growth(word.activeWord), growth(word.spokenWord)]);
      const fontSize = fitCaptionFontSize({
        words: line.words.map((word) => word.text),
        size: style.size ?? 64,
        maxWidth: style.maxWidth ?? maxWidth,
        uppercase: style.transform === 'uppercase' || line.words.every((word) => word.text === word.text.toUpperCase()),
        weight: style.weight,
        letterSpacing: style.letterSpacing,
        strokeWidth: style.stroke ? style.strokeWidth ?? 1 : 0,
        wordScale: Math.max(1, ...grown.map((each) => each.scale)),
        wordPadding: Math.max(0, ...grown.map((each) => each.padding)),
      });
      return <TextStyleContent key={`${line.style.id}-${index}`} tier={line.style.id}
        style={{ ...style, size: fontSize, ...(gradient ? { fill: undefined } : {}) }} keepWords>
        {line.words.map((word, i) => {
          // Words keep their place before they are spoken (layout never shifts).
          const enter = word.wordEnter ? evaluateCaptionPreset({ start: word.start, end: word.cueEnd, enter: word.wordEnter }, local) : null;
          const spoken = local >= word.start;
          const after = wordStyle(spoken ? word.spokenWord : undefined);
          const now = wordStyle(word.active ? word.activeWord : undefined);
          const motion = captionStateStyle(enter);
          const scale = after.scale * now.scale;
          return <React.Fragment key={word.index}>
            {i > 0 ? ' ' : null}
            <span data-motion-caption-word={word.index} data-motion-caption-active={word.active}
              style={{
                display: 'inline-block',
                ...(gradient ? textFillCss(gradient) : {}),
                ...after.css,
                ...now.css,
                ...motion,
                opacity: Number(motion.opacity ?? 1) * Number(now.css.opacity ?? after.css.opacity ?? 1),
                transform: [motion.transform, scale !== 1 ? `scale(${scale})` : null].filter(Boolean).join(' ') || undefined,
              }}>{word.text.trim()}</span>
          </React.Fragment>;
        })}
    </TextStyleContent>;
    })}
  </div>;
}
